import { Fragment, useMemo, useState } from "react";
import {
  Box,
  IconButton,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { sectionLabel } from "../../../components/cat15/labels";
import { formatNumber } from "../format";
import { common, financedEmissions } from "../../../i18n";
import { BorrowerListItem } from "../../../models/dto/cat15/cat15.types";
import {
  FeCell,
  FeHeadCell,
  FeTable,
  FeTableScroll,
  FeTableWrap,
} from "../../../components/cat15/FeTable";
import FePagination from "../../../components/cat15/FePagination";
import {
  FeChevronIcon,
  FeSortIcon,
  FeStatusDotIcon,
  FeVisibilityIcon,
} from "../../../components/cat15/FeIcons";

/**
 * Borrower list as a sector-grouped table — Figma SME-D-Bank "Hierarchical
 * Table" (node 7244:21482). Rows are bucketed by ISIC sector under a slate
 * header that collapses, and the pager pages over SECTORS, not rows.
 *
 * Column widths come from the Figma auto-layout: six `flex:1` columns plus a
 * fixed 72px action column, which `tableLayout: fixed` reproduces exactly.
 */

/** Sort keys — only columns the list endpoint actually returns data for. */
type SortKey = "name" | "taxId" | "borrowerType" | "loanCount" | "financed" | "status";
type SortDir = "asc" | "desc";

/**
 * Thai 13-digit IDs encode the entity kind in the first digit: `0` is a
 * juristic person (เลขทะเบียนนิติบุคคล), 1-8 a natural person's citizen ID.
 * The list endpoint has no explicit type field, so derive it from taxId —
 * which matches every row in the Figma design.
 */
export const isLegalEntity = (taxId: string) =>
  taxId.trim().startsWith("0");

interface Props {
  items: BorrowerListItem[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  /** Prefer the Thai name when the UI is in Thai. */
  preferThai?: boolean;
}

const BorrowerSectorTable = ({
  items,
  selectedId,
  onSelect,
  preferThai = false,
}: Props) => {
  const { t } = useTranslation([financedEmissions, common], {
    nsMode: "fallback",
  });

  const [sortKey, setSortKey] = useState<SortKey>("name");
  const [sortDir, setSortDir] = useState<SortDir>("asc");
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const [page, setPage] = useState(0);
  const [sectorsPerPage, setSectorsPerPage] = useState(6);

  const displayName = (b: BorrowerListItem) =>
    (preferThai ? b.nameTh || b.name : b.nameEn || b.name) ?? b.name;

  /** Sector buckets, each internally sorted by the active column. */
  const groups = useMemo(() => {
    // Bucket by ISIC section code (A-U); the header reads "C - Manufacturing"
    // like Figma 7244:21488, in the page language. No section sorts last.
    const bySector = new Map<string, BorrowerListItem[]>();
    const labelOf = new Map<string, string>();
    items.forEach((b) => {
      const key = b.sectionCode || "";
      const bucket = bySector.get(key);
      if (bucket) bucket.push(b);
      else bySector.set(key, [b]);
      if (!labelOf.has(key)) {
        const name = sectionLabel(b, preferThai ? "th" : "en", t("Sector-Unclassified"));
        labelOf.set(key, key ? `${key} - ${name}` : name);
      }
    });

    const dir = sortDir === "asc" ? 1 : -1;
    const compare = (a: BorrowerListItem, b: BorrowerListItem) => {
      switch (sortKey) {
        case "taxId":
          return a.taxId.localeCompare(b.taxId) * dir;
        case "loanCount":
          return (a.loanCount - b.loanCount) * dir;
        // A borrower with no figure sorts as the lowest.
        case "financed":
          return ((a.financedEmissions ?? -1) - (b.financedEmissions ?? -1)) * dir;
        case "status":
          return (Number(a.activated) - Number(b.activated)) * dir;
        case "borrowerType":
          return (
            (Number(isLegalEntity(a.taxId)) - Number(isLegalEntity(b.taxId))) *
            dir
          );
        default:
          return displayName(a).localeCompare(displayName(b)) * dir;
      }
    };

    return Array.from(bySector.entries())
      .sort(([a], [b]) => (!a ? 1 : !b ? -1 : a.localeCompare(b)))
      .map(([code, rows]) => ({
        sector: labelOf.get(code) ?? code,
        rows: [...rows].sort(compare),
      }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, sortKey, sortDir, preferThai, t]);

  const pageGroups = groups.slice(
    page * sectorsPerPage,
    (page + 1) * sectorsPerPage,
  );

  const toggleSort = (key: SortKey) => {
    if (key === sortKey) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  /** Figma head cell: 12px padding (24px leading on the first column), the
   *  label and its 12px sort glyph in a 4px-gap row. */
  const headCell = (
    label: string,
    opts: { sortKey?: SortKey; first?: boolean; width?: string } = {},
  ) => (
    <FeHeadCell
      sx={{
        p: "12px",
        ...(opts.first && { pl: "24px", pr: "12px" }),
        ...(opts.width && { width: opts.width }),
      }}
    >
      <Box
        display="flex"
        alignItems="center"
        gap="4px"
        sx={opts.sortKey ? { cursor: "pointer" } : undefined}
        onClick={opts.sortKey ? () => toggleSort(opts.sortKey!) : undefined}
      >
        {label}
        {opts.sortKey && (
          <FeSortIcon
            sx={{
              fontSize: "12px",
              opacity: opts.sortKey === sortKey ? 1 : 0.5,
            }}
          />
        )}
      </Box>
    </FeHeadCell>
  );

  return (
    <FeTableWrap>
      <FeTableScroll>
        <FeTable sx={{ tableLayout: "fixed", minWidth: "1000px" }}>
          <TableHead>
            <TableRow>
              {headCell(t("Col-Borrower"), { sortKey: "name", first: true })}
              {headCell(t("Tax-ID"), { sortKey: "taxId" })}
              {headCell(t("Col-Borrower-Type"), { sortKey: "borrowerType" })}
              {headCell(t("Active-Loans"), { sortKey: "loanCount" })}
              {headCell(t("Col-Finance-Emission"), { sortKey: "financed" })}
              {headCell(t("Col-Borrower-Status"), { sortKey: "status" })}
              {headCell(t("Col-Action"), { width: "72px" })}
            </TableRow>
          </TableHead>

          {pageGroups.map(({ sector, rows }) => {
            const isCollapsed = !!collapsed[sector];
            return (
              <Fragment key={sector}>
                <TableBody>
                  <TableRow>
                    {/* Sector header — pl12 / pr24 / py10, 12px gap, 44px tall. */}
                    <TableCell
                      colSpan={7}
                      sx={{
                        bgcolor: "var(--sector-header-bg)",
                        border: 0,
                        p: "10px 24px 10px 12px",
                      }}
                    >
                      <Box
                        display="flex"
                        alignItems="center"
                        gap="12px"
                        sx={{ cursor: "pointer" }}
                        onClick={() =>
                          setCollapsed((c) => ({ ...c, [sector]: !isCollapsed }))
                        }
                      >
                        <FeChevronIcon
                          sx={{
                            fontSize: "24px",
                            color: "var(--white)",
                            transform: isCollapsed
                              ? "rotate(-90deg)"
                              : "rotate(0deg)",
                            transition: "transform 150ms",
                          }}
                        />
                        <Typography
                          flex={1}
                          fontSize="14px"
                          fontWeight={500}
                          lineHeight="24px"
                          color="var(--white)"
                        >
                          {sector}
                        </Typography>
                        <Box
                          px="8px"
                          py="2px"
                          borderRadius="10px"
                          bgcolor="var(--sector-count-bg)"
                          flexShrink={0}
                        >
                          <Typography
                            fontSize="12px"
                            fontWeight={400}
                            lineHeight="normal"
                            color="var(--white)"
                          >
                            {rows.length}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                  </TableRow>
                </TableBody>

                {!isCollapsed && (
                  <TableBody>
                    {rows.map((b, i) => (
                      <TableRow
                        key={b.id}
                        selected={b.id === selectedId}
                        sx={{
                          // Zebra restarts inside every sector: first row white.
                          bgcolor:
                            i % 2 === 0 ? "var(--white)" : "var(--gray6)",
                          "&.Mui-selected": { bgcolor: "var(--green3)" },
                          "&.Mui-selected:hover": { bgcolor: "var(--green3)" },
                        }}
                      >
                        {/* Figma keeps names on one line (nowrap) but its mock names
                            are all short; clip long ones instead of letting them
                            run over the Tax ID column. Full name on hover. */}
                        <FeCell
                          title={displayName(b)}
                          sx={{
                            pl: "24px",
                            pr: "12px",
                            py: "12px",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {displayName(b)}
                        </FeCell>
                        <FeCell>{b.taxId}</FeCell>
                        <FeCell>
                          {isLegalEntity(b.taxId)
                            ? t("Borrower-Type-Legal")
                            : t("Borrower-Type-Individual")}
                        </FeCell>
                        <FeCell>{b.loanCount}</FeCell>
                        {/* tCO₂e for the bank's reporting year; "—" when no loan is in the calc. */}
                        <FeCell>
                          {b.financedEmissions == null
                            ? "—"
                            : `${formatNumber(b.financedEmissions)} tCO₂e`}
                        </FeCell>
                        {/* Borrower Status: Active (green) once the borrower's
                            first user has signed in, otherwise Inactive (grey).
                            Figma also shows "Deactivated", but nothing records a
                            deactivation yet, so it never appears. */}
                        <FeCell>
                          <Box display="flex" alignItems="center">
                            <Box
                              width="24px"
                              height="24px"
                              display="flex"
                              alignItems="center"
                              justifyContent="center"
                              flexShrink={0}
                            >
                              <FeStatusDotIcon
                                sx={{
                                  fontSize: "12px",
                                  color: b.activated
                                    ? "var(--green)"
                                    : "var(--gray1)",
                                }}
                              />
                            </Box>
                            <Typography
                              fontSize="16px"
                              fontWeight={400}
                              lineHeight="24px"
                              color={b.activated ? "var(--green)" : "var(--gray1)"}
                            >
                              {b.activated
                                ? t("Status-Active")
                                : t("Status-Inactive")}
                            </Typography>
                          </Box>
                        </FeCell>
                        <FeCell sx={{ width: "72px", textAlign: "center" }}>
                          <IconButton
                            size="small"
                            aria-label={t("Cert-View-Action")}
                            onClick={() => onSelect(b.id)}
                            sx={{ p: 0, color: "var(--dark)" }}
                          >
                            <FeVisibilityIcon sx={{ fontSize: "24px" }} />
                          </IconButton>
                        </FeCell>
                      </TableRow>
                    ))}
                  </TableBody>
                )}
              </Fragment>
            );
          })}
        </FeTable>
      </FeTableScroll>

      <FePagination
        count={groups.length}
        page={page}
        rowsPerPage={sectorsPerPage}
        onPageChange={setPage}
        onRowsPerPageChange={(n) => {
          setSectorsPerPage(n);
          setPage(0);
        }}
        rowsPerPageOptions={[6, 12, 24]}
        label={t("Sectors-Per-Page")}
        startContent={
          <Typography
            fontSize="14px"
            fontWeight={400}
            lineHeight="24px"
            color="var(--gray1)"
          >
            {t("Table-Summary", {
              borrowers: items.length,
              sectors: groups.length,
            })}
          </Typography>
        }
      />
    </FeTableWrap>
  );
};

export default BorrowerSectorTable;
